import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { blogArticles } from './Blog';

const BlogPost = () => {
  const { slug } = useParams();
  const article = blogArticles.find((a) => a.slug === slug);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    const currentIndex = blogArticles.findIndex((a) => a.slug === slug);
    const others = blogArticles.filter((_, i) => i !== currentIndex);
    // Pick 3 nearby articles (prefer adjacent for topical relevance)
    const picks = [];
    if (currentIndex > 0) picks.push(blogArticles[currentIndex - 1]);
    if (currentIndex < blogArticles.length - 1) picks.push(blogArticles[currentIndex + 1]);
    for (const a of others) {
      if (picks.length >= 3) break;
      if (!picks.includes(a)) picks.push(a);
    }
    return picks.slice(0, 3);
  }, [slug, article]);

  if (!article) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Article Not Found</h2>
        <Link to="/blog" className="text-blue-600 hover:underline">← Back to Blog</Link>
      </div>
    );
  }

  // Simple markdown-ish rendering for our static content
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('## ')) {
        elements.push(<h2 key={i} className="text-2xl font-bold text-gray-900 mt-8 mb-4">{line.slice(3)}</h2>);
      } else if (line.startsWith('### ')) {
        elements.push(<h3 key={i} className="text-xl font-semibold text-gray-900 mt-6 mb-3">{line.slice(4)}</h3>);
      } else if (line.startsWith('- **')) {
        elements.push(
          <li key={i} className="ml-6 mb-2 text-gray-700 list-disc">
            <strong>{line.match(/\*\*(.*?)\*\*/)?.[1]}</strong>
            {line.replace(/- \*\*.*?\*\*/, '').replace(/:/,':')}
          </li>
        );
      } else if (line.startsWith('- ')) {
        elements.push(<li key={i} className="ml-6 mb-2 text-gray-700 list-disc">{line.slice(2)}</li>);
      } else if (line.match(/^\d+\. /)) {
        elements.push(<li key={i} className="ml-6 mb-2 text-gray-700 list-decimal">{line.replace(/^\d+\. /, '')}</li>);
      } else if (line.startsWith('**Example')) {
        elements.push(<blockquote key={i} className="border-l-4 border-blue-400 pl-4 my-4 text-gray-700 italic">{line.replace(/\*\*/g, '')}</blockquote>);
      } else if (line.trim() === '') {
        // skip
      } else {
        elements.push(<p key={i} className="text-gray-700 leading-relaxed mb-4">{line}</p>);
      }
      i++;
    }
    return elements;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Helmet>
        <title>{article.title} | AppReview.ai Blog</title>
        <meta name="description" content={article.excerpt} />
        <link rel="canonical" href={`https://appreview.ai/blog/${article.slug}`} />
        <meta property="og:url" content={`https://appreview.ai/blog/${article.slug}`} />
        <meta property="og:title" content={`${article.title} | AppReview.ai Blog`} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:author" content="AppReview.ai" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} | AppReview.ai Blog`} />
        <meta name="twitter:description" content={article.excerpt} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": article.title,
          "description": article.excerpt,
          "datePublished": article.date,
          "author": { "@type": "Organization", "name": "AppReview.ai", "url": "https://appreview.ai" },
          "publisher": { "@type": "Organization", "name": "AppReview.ai", "url": "https://appreview.ai" },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://appreview.ai/blog/${article.slug}` },
          "url": `https://appreview.ai/blog/${article.slug}`
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://appreview.ai/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://appreview.ai/blog" },
            { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://appreview.ai/blog/${article.slug}` }
          ]
        })}</script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Blog
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-6 sm:p-10">
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <span>{new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span className="mx-2">•</span>
            <span>{article.readTime}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">{article.title}</h1>
          <div className="prose prose-lg max-w-none">
            {renderContent(article.content)}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to analyze your app reviews?</h3>
              <p className="text-gray-600 mb-4">Get AI-powered insights from your App Store and Google Play reviews in minutes.</p>
              <Link to="/" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all">
                Start Free Analysis
              </Link>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-10 pt-8 border-t border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Related Articles</h3>
              <div className="grid gap-4">
                {relatedArticles.map((related) => (
                  <Link key={related.slug} to={`/blog/${related.slug}`} className="group flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base font-semibold text-gray-900 group-hover:text-blue-700 transition-colors truncate">{related.title}</h4>
                      <p className="text-sm text-gray-500 mt-1">{related.date} · {related.readTime}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 ml-4 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
